import { useRef, useState } from "react";
import { ImagePlus, Loader2, Sparkles } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import MediaLibraryPicker from "@/components/admin/MediaLibraryPicker";
import AiTextButton from "@/components/admin/AiTextButton";

const inputClass =
  "h-11 w-full rounded-md border border-clay bg-white px-3 text-sm text-bark placeholder:text-bark/35 focus:border-terracotta focus:outline-none transition-colors duration-300";

const ProductForm = ({ categories, onCreated }) => {
  const [form, setForm] = useState({
    name: "",
    category: categories[0] || "",
    newCategory: "",
    price: "",
    delivery_estimate: "",
    shipping_fee: "",
    badge: "",
    description: "",
  });
  const [image, setImage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isNewCat = form.category === "__new__";

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImage(data.url);
      toast.success("Photo téléchargée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const generateDescription = async () => {
    if (!form.name.trim()) {
      toast.error("Saisissez d'abord le nom du produit");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/ai/product-description", {
        name: form.name.trim(),
        category: isNewCat ? form.newCategory.trim() : form.category,
        price: parseInt(form.price, 10) || null,
      });
      setForm((f) => ({ ...f, description: data.description }));
      toast.success("Description générée — vous pouvez la modifier");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const category = isNewCat ? form.newCategory.trim() : form.category;
    if (!category) {
      toast.error("Choisissez une catégorie ou créez-en une nouvelle");
      return;
    }
    if (!image) {
      toast.error("Ajoutez une photo du produit");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/products", {
        name: form.name,
        category,
        price: parseInt(form.price, 10),
        shipping_fee: parseInt(form.shipping_fee || "0", 10),
        image,
        images: [image],
        description: form.description,
        badge: form.badge || null,
        delivery_estimate: form.delivery_estimate,
        features: [],
      });
      toast.success(`Produit créé dans « ${category} »`);
      onCreated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="admin-product-form" className="mt-6 grid gap-3 border border-clay bg-cream p-6 sm:grid-cols-2">
      <div className="flex items-center gap-2">
        <input required placeholder="Nom du produit *" value={form.name} onChange={set("name")} className={inputClass} data-testid="admin-create-name-input" />
        <AiTextButton
          kind="product_name"
          testid="admin-create-name-ai-button"
          context={() => `${isNewCat ? form.newCategory : form.category}${form.description ? " — " + form.description : ""}`}
          onGenerated={(t) => setForm((f) => ({ ...f, name: t }))}
        />
      </div>
      <select value={form.category} onChange={set("category")} className={inputClass} data-testid="admin-create-category-select">
        {categories.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
        <option value="__new__">+ Nouvelle catégorie…</option>
      </select>
      {isNewCat && (
        <input
          required
          placeholder="Nom de la nouvelle catégorie (ex : Robinetterie extérieure) *"
          value={form.newCategory}
          onChange={set("newCategory")}
          className={`${inputClass} border-terracotta sm:col-span-2`}
          data-testid="admin-create-new-category-input"
        />
      )}
      <input required type="number" min="0" placeholder="Prix en FCFA *" value={form.price} onChange={set("price")} className={inputClass} data-testid="admin-create-price-input" />
      <input placeholder="Délai de livraison estimé (ex : 2 à 3 jours)" value={form.delivery_estimate} onChange={set("delivery_estimate")} className={inputClass} data-testid="admin-create-delivery-estimate-input" />
      <input type="number" min="0" placeholder="Frais de livraison France en FCFA (ex : 4500)" value={form.shipping_fee} onChange={set("shipping_fee")} className={inputClass} data-testid="admin-create-shipping-fee-input" />
      <input placeholder="Badge (ex : Nouveau)" value={form.badge} onChange={set("badge")} className={inputClass} data-testid="admin-create-badge-input" />

      <div className="flex items-center gap-4 sm:col-span-2">
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="admin-create-file-input" onChange={onFile} />
        <div className="h-20 w-16 shrink-0 overflow-hidden border border-clay bg-white">
          {image ? (
            <img src={image} alt="Aperçu" data-testid="admin-create-image-preview" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-clay">
              <ImagePlus className="h-5 w-5" />
            </div>
          )}
        </div>
        <button
          type="button"
          data-testid="admin-create-upload-button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex h-11 flex-1 items-center justify-center gap-2 rounded-full bg-bark text-xs font-bold uppercase tracking-[0.15em] text-sand transition-colors hover:bg-terracotta disabled:opacity-50"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          {uploading ? "Envoi en cours…" : image ? "Changer la photo" : "Télécharger la photo *"}
        </button>
        <MediaLibraryPicker testid="admin-create-media-library" onSelect={(url) => setImage(url)} />
      </div>

      <div className="sm:col-span-2">
        <button
          type="button"
          data-testid="admin-create-ai-description-button"
          onClick={generateDescription}
          disabled={generating}
          className="mb-2 flex h-9 items-center gap-2 rounded-full border border-terracotta px-4 text-[10px] font-bold uppercase tracking-widest text-terracotta transition-colors hover:bg-terracotta hover:text-sand disabled:opacity-50"
        >
          {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          {generating ? "Rédaction en cours…" : "Générer la description avec l'IA"}
        </button>
        <textarea placeholder="Description" value={form.description} onChange={set("description")} className="min-h-[80px] w-full rounded-md border border-clay bg-white px-3 py-3 text-sm text-bark placeholder:text-bark/35 focus:border-terracotta focus:outline-none" data-testid="admin-create-description-input" />
      </div>
      <button
        type="submit"
        disabled={saving || uploading}
        className="flex h-11 items-center justify-center gap-2 rounded-full bg-terracotta text-xs font-bold uppercase tracking-[0.15em] text-sand transition-colors hover:bg-terracotta-dark disabled:opacity-50 sm:col-span-2"
        data-testid="admin-create-submit-button"
      >
        {saving && <Loader2 className="h-4 w-4 animate-spin" />} Créer le produit
      </button>
    </form>
  );
};

export default ProductForm;
